import React, { useState, useEffect } from 'react';
import api from '../../../../services/api';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Divider, 
  FormControl, 
  FormHelperText, 
  Grid, 
  InputLabel, 
  MenuItem, 
  Select, 
  TextField, 
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import rfidService, { Vehicle } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';

interface ManufacturerOption {
  id: string;
  name: string;
}

interface ModelOption {
  id: string;
  name: string;
  manufacturer_id: string;
}

const VehicleForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [manufacturers, setManufacturers] = useState<ManufacturerOption[]>([]);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [filteredModels, setFilteredModels] = useState<ModelOption[]>([]);
  const [loadingManufacturers, setLoadingManufacturers] = useState<boolean>(false);
  const [loadingModels, setLoadingModels] = useState<boolean>(false);

  const [formData, setFormData] = useState<Partial<Vehicle>>({
    name: '',
    registration_number: '',
    manufacturer: null,
    model: null,
    description: null,
    epc_id: null,
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchManufacturers();
    fetchModels();
    
    if (isEdit && id) {
      fetchVehicle(id);
    }
  }, [isEdit, id]);

  useEffect(() => {
    console.log('Filtering models based on manufacturer:', formData.manufacturer, models);
    if (formData.manufacturer) {
      setFilteredModels(models.filter(model => model.manufacturer_id === formData.manufacturer));
    } else {
      setFilteredModels([]);
    }
  }, [formData.manufacturer, models]);

  const fetchVehicle = async (vehicleId: string) => {
    try {
      setLoading(true);
      const data = await rfidService.getVehicleById(vehicleId);
      setFormData({
        name: data.name,
        registration_number: data.registration_number,
        manufacturer: data.manufacturer,
        model: data.model,
        description: data.description,
        epc_id: data.epc_id,
        company: data.company
      });
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Failed to fetch vehicle details');
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async () => {
    try {
      setLoadingManufacturers(true);
      // Fetch manufacturers from the API
      const response = await api.get('/assets/manufacturers/', {
        params: {
          page: 1,
          page_size: 25,
          sort_by: 'name',
          sort_order: 'asc',
          dropdown: true,
        }
      });
      setManufacturers(response.data.results || []);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      toast.error('Failed to fetch manufacturers');
    } finally {
      setLoadingManufacturers(false);
    }
  };

  const fetchModels = async () => {
    try {
      setLoadingModels(true);
      // Fetch models from the API
      const response = await api.get('/master/models/', {
        params: {
          page: 1,
          page_size: 25,
          sort_by: 'name',
          sort_order: 'asc',
          dropdown: true,
        }
      });
      setModels(response.data.results || []);
    } catch (error) {
      console.error('Error fetching models:', error);
      toast.error('Failed to fetch models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | { target: { name?: string; value: unknown } }) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!formData.registration_number) {
      newErrors.registration_number = 'Registration number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);

      if (isEdit && id) {
        const { company, ...updateData } = formData;
        await rfidService.updateVehicle(id, updateData);
        toast.success('Vehicle updated successfully');
      } else {
        await rfidService.createVehicle(formData);
        toast.success('Vehicle created successfully');
      }
      
      navigate('/rfid/vehicles');
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(isEdit ? 'Failed to update vehicle' : 'Failed to create vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }} >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/rfid/vehicles"
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Vehicle' : 'Create Vehicle'}
          </Typography>
        </Box>
      </Box>

      <Card sx={{ p: 2 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vehicle Name"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Registration Number"
                  name="registration_number"
                  value={formData.registration_number || ''}
                  onChange={handleChange}
                  error={!!errors.registration_number}
                  helperText={errors.registration_number}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingManufacturers}>
                  <InputLabel id="manufacturer-label">Manufacturer</InputLabel>
                  <Select
                    labelId="manufacturer-label"
                    name="manufacturer"
                    value={formData.manufacturer || ''}
                    onChange={handleChange}
                    label="Manufacturer"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {manufacturers.map((manufacturer) => (
                      <MenuItem key={manufacturer.id} value={manufacturer.id}>
                        {manufacturer.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {loadingManufacturers 
                      ? 'Loading manufacturers...' 
                      : 'Select the manufacturer of the vehicle'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingModels || !formData.manufacturer}>
                  <InputLabel id="model-label">Model</InputLabel>
                  <Select
                    labelId="model-label"
                    name="model"
                    value={formData.model || ''}
                    onChange={handleChange}
                    label="Model"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {filteredModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {loadingModels 
                      ? 'Loading models...' 
                      : !formData.manufacturer
                        ? 'Select a manufacturer first'
                        : 'Select the model of the vehicle'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  helperText="Optional: Add a description for the vehicle"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="EPC ID"
                  name="epc_id"
                  value={formData.epc_id || ''}
                  onChange={handleChange}
                  helperText="Optional: RFID tag EPC ID for the vehicle"
                />
              </Grid>
              
            </Grid>
            <Grid item xs={12} md={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    component={Link}
                    to="/rfid/vehicles"
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        {isEdit ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      isEdit ? 'Update Vehicle' : 'Create Vehicle'
                    )}
                  </Button>
                </Box>
              </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VehicleForm;